/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit'
import { uniq } from 'lodash'

import { BULK_MOVE_MAP_LAYERS, updateFromMarkers, updateToMarkers } from 'components/route-manager/BulkActions/settings'
import { serializeDate } from 'utils/date'

import type { CaseReducer, PayloadAction, SliceCaseReducers } from '@reduxjs/toolkit'

const { BOTH_ROUTES_LAYER } = BULK_MOVE_MAP_LAYERS

export const getInitialBulkActionState = (mapLayer: string = BOTH_ROUTES_LAYER) => ({
  focus: {
    name: '',
    rowIndex: '' as string | number,
  },
  invalidStopsToBeAssignedCount: 0,
  isConfirmCloseDialogOpen: false,
  isSelectDateDialogOpen: false,
  isOpen: false,
  mapLayer,
  serializedServiceDate: serializeDate(new Date()),
  moveFromRouteId: '',
  moveToRouteId: '',
  moveFromRouteStopMarkers: [] as unknown[],
  moveFromRows: [] as unknown[],
  // Declared so selectors return a stable reference before the geojson loads (default-value trap).
  moveFromRouteContainerFeatures: [] as unknown[],
  moveToRouteContainerFeatures: [] as unknown[],
  moveToRouteSequenceLine: {} as Record<string, unknown>,
  moveToRouteStopMarkers: [] as unknown[],
  moveToRows: [] as unknown[],
  routesById: {} as Record<string, unknown>,
  selectedMoveFromRows: [] as string[],
  selectedMoveToRows: [] as string[],
  toBeInsertedRowsIds: [] as string[],
  validStopIdsToBeAssigned: [] as string[],
  isSequenceDialogOpen: false,
  fitBounds: true,
  confirmPermanentMoveDialog: {
    isOpen: false,
  },
  fetchId: '',
  moveFromRouteSequenceLine: {} as Record<string, unknown>,
  serializedFromDate: serializeDate(new Date()),
  serializedToDate: serializeDate(new Date()),
  toSerializedDate: null as string | null,
  fromSerializedDate: null as string | null,
  routeId: null as string | null,
})

// The index signature reflects setBulkState's schemaless merge, which dialogs use to stash extra fields.
export type BulkActionState = ReturnType<typeof getInitialBulkActionState> & Record<string, unknown>

interface CreateBulkActionSliceArgs<R extends SliceCaseReducers<BulkActionState>> {
  name: string
  // Never narrowed to the per-slice literal: setBulkState lets a dialog stash further fields.
  initialState?: Partial<BulkActionState>
  reducers?: R
}

export const createBulkActionSlice = <R extends SliceCaseReducers<BulkActionState> = SliceCaseReducers<BulkActionState>>({
  name,
  initialState,
  reducers,
}: CreateBulkActionSliceArgs<R>) => {
  const setBulkState: CaseReducer<BulkActionState, PayloadAction<Partial<BulkActionState>>> = (state, action) => ({
    ...state,
    ...action.payload,
  })

  const setBulkDate: CaseReducer<BulkActionState, PayloadAction<string | Date | null | undefined>> = (state, action) => {
    const newSerializedServiceDate = action.payload
    if (!newSerializedServiceDate) {
      state.serializedServiceDate = serializeDate(new Date())
    } else if (newSerializedServiceDate instanceof Date) {
      state.serializedServiceDate = serializeDate(newSerializedServiceDate)
    } else {
      state.serializedServiceDate = newSerializedServiceDate
    }
  }

  // Explicitly trigger fitBounds again
  const triggerFitBounds: CaseReducer<BulkActionState> = state => {
    state.fitBounds = true
  }

  const addSelectedMoveFromRows: CaseReducer<BulkActionState, PayloadAction<string[]>> = (state, action) => {
    state.fitBounds = false
    state.selectedMoveFromRows = uniq([...state.selectedMoveFromRows, ...action.payload])
    state.moveFromRouteStopMarkers = updateFromMarkers(state.selectedMoveFromRows, state.routesById, state.moveFromRouteStopMarkers)
  }

  const addSelectedMoveToRows: CaseReducer<BulkActionState, PayloadAction<string[]>> = (state, action) => {
    state.fitBounds = false
    state.selectedMoveToRows = uniq([...state.selectedMoveToRows, ...action.payload])
    state.moveToRouteStopMarkers = updateToMarkers(
      state.selectedMoveToRows,
      state.routesById,
      state.moveToRouteStopMarkers,
      state.moveToRouteId
    )
  }

  const removeSelectedMoveFromRows: CaseReducer<BulkActionState, PayloadAction<string[]>> = (state, action) => {
    state.fitBounds = false
    const idsToRemove = new Set(action.payload)
    state.selectedMoveFromRows = state.selectedMoveFromRows.filter(id => !idsToRemove.has(id))
    state.moveFromRouteStopMarkers = updateFromMarkers(state.selectedMoveFromRows, state.routesById, state.moveFromRouteStopMarkers)
  }

  const removeSelectedMoveToRows: CaseReducer<BulkActionState, PayloadAction<string[]>> = (state, action) => {
    state.fitBounds = false
    const idsToRemove = new Set(action.payload)
    state.selectedMoveToRows = state.selectedMoveToRows.filter(id => !idsToRemove.has(id))
    state.moveToRouteStopMarkers = updateToMarkers(
      state.selectedMoveToRows,
      state.routesById,
      state.moveToRouteStopMarkers,
      state.moveToRouteId
    )
  }

  const toggleSelectedMoveFromRow: CaseReducer<BulkActionState, PayloadAction<string>> = (state, action) => {
    state.fitBounds = false
    const id = action.payload
    const isSelected = state.selectedMoveFromRows.includes(id)
    state.selectedMoveFromRows = isSelected ? state.selectedMoveFromRows.filter(r => r !== id) : [...state.selectedMoveFromRows, id]
    state.moveFromRouteStopMarkers = updateFromMarkers(state.selectedMoveFromRows, state.routesById, state.moveFromRouteStopMarkers)
  }

  const toggleSelectedMoveToRow: CaseReducer<BulkActionState, PayloadAction<string>> = (state, action) => {
    state.fitBounds = false
    const id = action.payload
    const isSelected = state.selectedMoveToRows.includes(id)
    state.selectedMoveToRows = isSelected ? state.selectedMoveToRows.filter(r => r !== id) : [...state.selectedMoveToRows, id]
    state.moveToRouteStopMarkers = updateToMarkers(
      state.selectedMoveToRows,
      state.routesById,
      state.moveToRouteStopMarkers,
      state.moveToRouteId
    )
  }

  const setSelectedMoveFromRows: CaseReducer<BulkActionState, PayloadAction<string[]>> = (state, action) => {
    state.selectedMoveFromRows = action.payload
    state.moveFromRouteStopMarkers = updateFromMarkers(state.selectedMoveFromRows, state.routesById, state.moveFromRouteStopMarkers)
  }

  const setSelectedMoveToRows: CaseReducer<BulkActionState, PayloadAction<string[]>> = (state, action) => {
    state.selectedMoveToRows = action.payload
    state.moveToRouteStopMarkers = updateToMarkers(
      state.selectedMoveToRows,
      state.routesById,
      state.moveToRouteStopMarkers,
      state.moveToRouteId
    )
  }

  const setConfirmPermanentMoveDialogState: CaseReducer<BulkActionState, PayloadAction<Record<string, unknown>>> = (state, action) => {
    state.confirmPermanentMoveDialog = {
      ...state.confirmPermanentMoveDialog,
      ...action.payload,
    }
  }

  const resetBulkState: CaseReducer<BulkActionState, PayloadAction<Partial<BulkActionState>>> = (_state, action) => ({
    ...getInitialBulkActionState(),
    ...action.payload,
  })

  return createSlice({
    name,
    initialState: { ...getInitialBulkActionState(), ...initialState } as BulkActionState,
    reducers: {
      setBulkState,
      setBulkDate,
      triggerFitBounds,
      addSelectedMoveFromRows,
      addSelectedMoveToRows,
      removeSelectedMoveFromRows,
      removeSelectedMoveToRows,
      toggleSelectedMoveFromRow,
      toggleSelectedMoveToRow,
      setSelectedMoveFromRows,
      setSelectedMoveToRows,
      setConfirmPermanentMoveDialogState,
      resetBulkState,
      ...reducers,
    },
  })
}
